import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, Divider, Flex, Input, Radio } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ContentType } from '@pages/Clients/NewClientsList/components/MultiFilter/types';

import { useNewClientsListContext } from '@pages/Clients/NewClientsList/context/useNewClientsListContext';
import {
  HAS_TAGS_RADIO_OPTIONS,
  PF,
} from '@pages/Clients/NewClientsList/data/constants';
import { FilterWrapper } from '@pages/Clients/NewClientsList/ui';

export const RecordsTagsFilter: ContentType = ({ open, onClose, id }) => {
  const [localSearch, setLocalSearch] = useState<string>('');
  const [localSelectedTags, setLocalSelectedTags] = useState<number[]>([]);
  const [localIsIncludeTags, setLocalIsIncludeTags] = useState<boolean>(true);

  const {
    recordsTagsOptions,
    onChangeIsIncludeRecordsTags,
    onChangeRecordsTags,
    filtersMap,
  } = useNewClientsListContext();

  const { recordsTags, isIncludeRecordsTags } = filtersMap.get(id)!;

  const onConfirm = useCallback(() => {
    onChangeRecordsTags(localSelectedTags, id);
    onChangeIsIncludeRecordsTags(localIsIncludeTags, id);
    onClose?.();
  }, [
    localIsIncludeTags,
    localSelectedTags,
    onChangeIsIncludeRecordsTags,
    onChangeRecordsTags,
    onClose,
    id,
  ]);

  useEffect(() => {
    if (open) {
      setLocalSelectedTags(recordsTags);
      setLocalIsIncludeTags(isIncludeRecordsTags);
      setLocalSearch('');
    } else {
      setLocalSelectedTags([]);
      setLocalIsIncludeTags(true);
      setLocalSearch('');
    }
  }, [isIncludeRecordsTags, open, recordsTags]);

  const filteredTagsOptions = useMemo(() => {
    if (!localSearch) {
      return recordsTagsOptions;
    }

    return recordsTagsOptions.filter((tagOption) =>
      tagOption?.title
        ?.toString()
        ?.toLowerCase()
        ?.includes(localSearch?.toLowerCase())
    );
  }, [localSearch, recordsTagsOptions]);

  const visibleTagValuesSet = useMemo(() => {
    return new Set(
      filteredTagsOptions
        .map((opt) => opt.value)
        .filter((v): v is number => typeof v === 'number')
    );
  }, [filteredTagsOptions]);

  const optionsOrderMap = useMemo(() => {
    const map = new Map<number, number>();
    recordsTagsOptions.forEach((opt, index) => {
      if (typeof opt.value === 'number') map.set(opt.value, index);
    });
    return map;
  }, [recordsTagsOptions]);

  const onTagsChange = useCallback(
    (checkedValues: Array<string | number>) => {
      const checkedVisible = checkedValues.filter(
        (v): v is number => typeof v === 'number'
      );

      setLocalSelectedTags((prev) => {
        const hiddenSelected = prev.filter((v) => !visibleTagValuesSet.has(v));
        const merged = Array.from(
          new Set([...hiddenSelected, ...checkedVisible])
        );

        merged.sort(
          (a, b) =>
            (optionsOrderMap.get(a) ?? Number.MAX_SAFE_INTEGER) -
            (optionsOrderMap.get(b) ?? Number.MAX_SAFE_INTEGER)
        );

        return merged;
      });
    },
    [optionsOrderMap, visibleTagValuesSet]
  );

  return (
    <FilterWrapper onConfirm={onConfirm}>
      <Flex vertical gap={8}>
        <Input
          placeholder="Поиск по тегам"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          prefix={<SearchOutlined />}
        />

        <div className={`${PF}__tags-container`}>
          <div className={`${PF}__tags-list`}>
            <Checkbox.Group
              value={localSelectedTags}
              options={filteredTagsOptions}
              onChange={onTagsChange}
              className={`${PF}__checkbox-group`}
            />
          </div>
        </div>

        <Divider className={`${PF}__record-tags-divider`} />

        <Radio.Group
          value={localIsIncludeTags}
          onChange={(e) => setLocalIsIncludeTags(e.target.value)}
          options={HAS_TAGS_RADIO_OPTIONS}
          className={`${PF}__radio-group`}
        />
      </Flex>
    </FilterWrapper>
  );
};
